import React from 'react';
// Component that removes whitespace-only text nodes from children
export const CleanContainer = ({ children, as: Component = 'div', ...props }) => {
  const cleanChildren = React.Children.map(children, (child) => {
    // If it's a string that's only whitespace (including newlines), don't render it
    if (typeof child === 'string') {
      // Only remove if it's COMPLETELY whitespace (don't trim partial text)
      if (child.trim() === '' || child === '\n' || child.match(/^\s*$/)) {
        return null;
      }
      // DON'T trim - preserve spaces for inline text
      return child;
    }
    return child;
  });
  
  // Filter out null/undefined children - handle case where cleanChildren is undefined
  const filteredChildren = cleanChildren ? cleanChildren.filter(child => child != null) : [];
  
  return <Component {...props}>{filteredChildren}</Component>;
};

// Simplified markdown components that work with our clean CSS
export const createCleanMarkdownComponents = (darkMode, startOffset = 0, CodeBlockComponent, codeBlockProps = {}) => {
  let currentOffset = startOffset;
  
  // Utility to add data attributes and track character positions
  const addDataAttributes = (children, additionalProps = {}) => {
    const textContent = typeof children === 'string' ? children : 
      React.Children.toArray(children).map(child => 
        typeof child === 'string' ? child : child.props?.children || ''
      ).join('');
    
    const start = currentOffset;
    const end = currentOffset + textContent.length;
    currentOffset = end + 1; // +1 for spacing between elements
    
    return {
      'data-start': start,
      'data-end': end,
      'data-content-length': textContent.length,
      ...additionalProps
    };
  };

  return {
    // Let CSS handle all the styling - just add data attributes
    h1({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return <h1 {...dataAttrs} {...props}>{children}</h1>;
    },
    h2({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return <h2 {...dataAttrs} {...props}>{children}</h2>;
    },
    h3({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return <h3 {...dataAttrs} {...props}>{children}</h3>;
    },
    h4({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return <h4 {...dataAttrs} {...props}>{children}</h4>;
    },
    h5({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return <h5 {...dataAttrs} {...props}>{children}</h5>;
    },
    h6({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return <h6 {...dataAttrs} {...props}>{children}</h6>;
    },
    
    // Paragraphs
    p({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return (
        <CleanContainer as="p" {...dataAttrs} {...props}>
          {children}
        </CleanContainer>
      );
    },
    
    // Lists
    ul({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return (
        <CleanContainer as="ul" {...dataAttrs} {...props}>
          {children}
        </CleanContainer>
      );
    },
    ol({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return (
        <CleanContainer as="ol" {...dataAttrs} {...props}>
          {children}
        </CleanContainer>
      );
    },
    li({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return (
        <CleanContainer as="li" {...dataAttrs} {...props}>
          {children}
        </CleanContainer>
      );
    },
    
    // Other elements - let CSS handle styling
    blockquote({ children, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return (
        <CleanContainer as="blockquote" {...dataAttrs} {...props}>
          {children}
        </CleanContainer>
      );
    },
    
    // Links
    a({ children, href, ...props }) {
      const dataAttrs = addDataAttributes(children);
      return (
        <a 
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          {...dataAttrs}
          {...props}
        >
          {children}
        </a>
      );
    },
    
    // Tables - minimal wrapper
    table({ children, ...props }) {
      return <table {...props}>{children}</table>;
    },
    thead({ children, ...props }) {
      return <thead {...props}>{children}</thead>;
    },
    th({ children, ...props }) {
      return <th {...props}>{children}</th>;
    },
    td({ children, ...props }) {
      return <td {...props}>{children}</td>;
    },
    
    // Simple elements
    hr({ ...props }) {
      return <hr {...props} />;
    },
    strong({ children, ...props }) {
      return <strong {...props}>{children}</strong>;
    },
    em({ children, ...props }) {
      return <em {...props}>{children}</em>;
    },
    
    // Code handling
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      if (!inline && CodeBlockComponent) {
        return <CodeBlockComponent language={language} darkMode={darkMode} {...codeBlockProps}>{children}</CodeBlockComponent>;
      }
      
      // Inline code - let CSS handle styling
      return <code {...props}>{children}</code>;
    },
    
    // Pre elements
    pre({ children, ...props }) {
      return <pre {...props}>{children}</pre>;
    }
  };
};

export const normalizeMarkdownBulletLists = (text = '') => {
  if (!text) {
    return '';
  }

  return text.replace(/^\s*•\s+/gm, '- ');
};

// Extract markdown link information
export const extractMarkdownLink = (text) => {
  if (!text) return null;
  
  const linkMatch = text.match(/\[(.+?)\]\((.+?)\)/);
  if (linkMatch && linkMatch.length >= 3) {
    return {
      fileName: linkMatch[1],
      fileUrl: linkMatch[2],
      fullMatch: linkMatch[0]
    };
  }
  
  return null;
};

// Check if text contains file links
export const hasFileLinks = (text) => {
  if (!text) return false;
  
  return text.includes('](http') && (
    text.includes('.pdf') || 
    text.includes('.doc') || 
    text.includes('.xlsx') ||
    text.includes('.txt')
  );
}; 